import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { stripeService } from '../../services/stripeService';
import { sanitizeError } from '../../utils/sanitizeError';
import { useLanguage } from '../../contexts/LanguageContext';

const InvoiceHistory = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const { data, error: invoiceError } = await stripeService?.getInvoices();
      if (invoiceError) throw invoiceError;
      setInvoices(data || []);
    } catch (err) {
      console.error('[InvoiceHistory] load invoices failed:', err);
      // sanitizeError surfaces actionable Stripe detail when present
      // (e.g. "No such customer" → reconnect message), otherwise falls
      // back to the i18n generic. Per card hsGzqWrI.
      setError(sanitizeError(err?.message, t('failedToLoadInvoices')));
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, currency) => {
    const value = (amount / 100)?.toFixed(2);
    const symbol = currency?.toUpperCase() === 'GBP' ? '£' : currency?.toUpperCase() === 'USD' ? '$' : currency?.toUpperCase() === 'EUR' ? '€' : '';
    return `${symbol}${value}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid:           'bg-green-100 text-green-700 border-green-200',
      open:           'bg-yellow-100 text-yellow-700 border-yellow-200',
      void:           'bg-gray-100 text-gray-600 border-gray-200',
      uncollectible:  'bg-red-100 text-red-700 border-red-200',
      draft:          'bg-blue-100 text-blue-700 border-blue-200',
    };
    return badges?.[status] || badges?.open;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 pt-20 pb-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate('/subscription-management')}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2 transition-colors"
            >
              <Icon name="ArrowLeft" size={14} />
              {t('backToSubscription')}
            </button>
            <h1 className="text-2xl font-bold text-foreground">{t('invoiceHistory')}</h1>
            <p className="text-muted-foreground mt-1">{t('invoiceHistorySubtitle')}</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent"></div>
          </div>
        ) : invoices?.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <div className="flex items-center justify-center w-14 h-14 bg-muted rounded-full mx-auto mb-4">
              <Icon name="FileText" size={24} className="text-muted-foreground" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-1">{t('noInvoicesYet')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('noInvoicesYetDesc')}
            </p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="grid grid-cols-5 gap-4 px-6 py-3 bg-muted/50 border-b border-border">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('invoice')}</span>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('date')}</span>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('period')}</span>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('amount')}</span>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('status')}</span>
            </div>

            {invoices?.map((invoice) => (
              <div
                key={invoice?.id}
                className="grid grid-cols-5 gap-4 px-6 py-4 border-b border-border last:border-0 hover:bg-muted/30 transition-colors items-center"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{invoice?.number || invoice?.id?.slice(-8)}</p>
                  {invoice?.description && (
                    <p className="text-xs text-muted-foreground truncate">{invoice?.description}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-foreground">{formatDate(invoice?.created)}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground">
                    {formatDate(invoice?.period_start)} – {formatDate(invoice?.period_end)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {formatAmount(invoice?.amount_paid || invoice?.amount_due, invoice?.currency)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${getStatusBadge(invoice?.status)}`}>
                    {invoice?.status}
                  </span>
                  {invoice?.invoice_pdf && (
                    <a
                      href={invoice?.invoice_pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:text-accent/80 transition-colors"
                      title={t('downloadPdf')}
                    >
                      <Icon name="Download" size={15} />
                    </a>
                  )}
                  {invoice?.hosted_invoice_url && (
                    <a
                      href={invoice?.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      title={t('viewInvoice')}
                    >
                      <Icon name="ExternalLink" size={15} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceHistory;
